import WMSCapabilities from 'ol/format/WMSCapabilities'
import { apiFetch } from '../api/client'
import { PROXY_BASE } from '../config'

/**
 * One advertised WMS layer. The proxy already filters GetCapabilities to the
 * company's allowed layers (PRD §5), so whatever is advertised here is
 * exactly what the user may see — zero viewer-side configuration.
 */
export interface LayerInfo {
  workspace: string
  /** Qualified layer name as advertised (e.g. "mining:canadian_claims") — used as the LAYERS param. */
  name: string
  title: string
  abstract: string
  queryable: boolean
  /** [west, south, east, north] in EPSG:4326, when advertised. */
  extent4326: [number, number, number, number] | null
  legendUrl: string
}

/** Build a GetLegendGraphic URL through the proxy (PRD P0.6 — legend per layer). */
function legendUrlFor(workspace: string, layerName: string): string {
  const params = new URLSearchParams({
    SERVICE: 'WMS',
    VERSION: '1.3.0',
    REQUEST: 'GetLegendGraphic',
    FORMAT: 'image/png',
    LAYER: layerName,
  })
  return `${PROXY_BASE}/geoserver/${workspace}/wms?${params.toString()}`
}

interface CapsLayerNode {
  Name?: string
  Title?: string
  Abstract?: string
  queryable?: boolean
  EX_GeographicBoundingBox?: number[]
  Layer?: CapsLayerNode[]
}

/**
 * Fetch and parse WMS GetCapabilities for one workspace, flattening the layer
 * tree into the named (i.e. requestable) layers.
 */
export async function fetchWorkspaceLayers(workspace: string): Promise<LayerInfo[]> {
  const res = await apiFetch(
    `/geoserver/${workspace}/wms?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetCapabilities`,
  )
  const text = await res.text()
  const caps = new WMSCapabilities().read(text) as {
    Capability?: { Layer?: CapsLayerNode }
  }

  const layers: LayerInfo[] = []
  const walk = (node: CapsLayerNode | undefined): void => {
    if (!node) return
    if (node.Name) {
      const bbox = node.EX_GeographicBoundingBox
      layers.push({
        workspace,
        name: node.Name,
        title: node.Title ?? node.Name,
        abstract: node.Abstract ?? '',
        queryable: node.queryable === true,
        extent4326:
          Array.isArray(bbox) && bbox.length === 4
            ? [bbox[0], bbox[1], bbox[2], bbox[3]]
            : null,
        legendUrl: legendUrlFor(workspace, node.Name),
      })
    }
    node.Layer?.forEach(walk)
  }
  walk(caps.Capability?.Layer)
  return layers
}
